/* eslint-disable react-hooks/exhaustive-deps */
import { tokens } from "../../../theme";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Close,
  Error,
  ExpandMore,
  InfoOutlined,
  Search,
} from "@mui/icons-material";
import CommitsList from "./CommitsList";
import ConvoyDialog from "../../../components/ConvoyDialog";
import { closeButtonStyles } from "../../../styles/buttonStyles";
import useResizableDrawerWidth from "../../../components/useResizableDrawerWidth";
import DrawerResizeHandle from "../../../components/DrawerResizeHandle";
import DrawerSnapToggle from "../../../components/DrawerSnapToggle";
import { useGetPatch } from "../../../api/releases";
import { useGetDCPrs } from "../../../api/pullRequests";
import { releaseStyles } from "../styles";
import {
  blockedReason,
  buildServices,
  doubleCommitPrs,
  filterServices,
  isCommitDouble,
  patchBanner,
  serviceBadges,
  serviceCounts,
  unbuiltSelectedHeads,
} from "../patchHelpers";

const SEARCH_DEBOUNCE_MS = 150;

// Patching is opt-in: nothing is selected until the user picks. Since selection
// is stored as a count skipped from the head down, "nothing selected" is every
// repo skipping all of its commits — an absent entry would mean the opposite.
const allSkipped = (repositories = []) =>
  Object.fromEntries(
    (repositories || []).map((repo) => [
      repo?.repo_name,
      (repo?.commits || []).length,
    ])
  );

// Every service starts open, so nothing has to be clicked before the commits
// that need ticking are on screen. Services with no commits never render an
// accordion, so they are left out.
const allExpandedNames = (repositories = []) =>
  new Set(
    (repositories || [])
      .filter((repo) => (repo?.commits || []).length)
      .map((repo) => repo?.repo_name)
  );

// Commits are included from the head downward, so a repo contributes
// everything below the commits skipped off the top. A repo with all commits
// skipped drops out of the payload entirely.
export const getPatchPayload = (repositories, skipped = {}) => {
  const payload = [];
  repositories.forEach((repo) => {
    const commits = (repo?.commits || [])
      .slice(skipped[repo?.repo_name] || 0)
      .map((commit) => structuredClone(commit));
    commits.length &&
      payload.push({
        repo_name: repo.repo_name,
        commits: commits,
      });
  });
  return { repositories: payload };
};

const PatchLabelDialog = ({
  selectedAppGroup,
  patchLabelDialog,
  createReleaseLabel,
  resetDialog,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.paletteId);
  const { DOUBLE_FG, INFO_FG, patchDrawer } = releaseStyles(colors);

  const { isOpen, release, releaseName, nextVersion, notReadOnly } =
    patchLabelDialog;

  const isDesktopDrawer = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const {
    width: drawerWidth,
    isAnimating: isDrawerResizing,
    isWide: isDrawerWide,
    toggleWide: toggleDrawerWide,
    handleProps: resizeHandleProps,
  } = useResizableDrawerWidth({
    storageKey: "create-patch",
    defaultWidth: 720,
    maxWidth: 1200,
    enabled: isDesktopDrawer,
  });

  const [repositories, setRepositories] = useState([]);
  const [expanded, setExpanded] = useState(new Set());
  const [segment, setSegment] = useState("all");
  const [searchInput, setSearchInput] = useState("");
  const [query, setQuery] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  // Commits are skipped from the top down, so selection is a count per repo
  // rather than a set: 0 means the whole repo ships, and a count equal to the
  // repo's commits means none of it does. Starts fully skipped — see allSkipped.
  const [skipped, setSkipped] = useState({});
  // "select" is the existing pick-your-commits screen; "review" is the new
  // read-only recap shown before the POST actually fires.
  const [step, setStep] = useState("select");
  const [hideLeftOut, setHideLeftOut] = useState(false);
  const [confirmClose, setConfirmClose] = useState(false);

  const searchRef = useRef(null);
  const step1ConfirmRef = useRef(null);
  const reviewConfirmRef = useRef(null);
  // Skips the very first step-focus pass on mount/reopen, so it doesn't fight
  // the SlideProps onEntered search-field focus below.
  const skipNextStepFocus = useRef(true);

  const {
    postData: getPatch,
    response: getPatchResp,
    isLoading: isPatchLoading,
  } = useGetPatch(selectedAppGroup);

  const { postData: getDcPrs, response: getDcPrsResp } = useGetDCPrs();

  useEffect(() => {
    if (getPatchResp?.repositories) {
      setRepositories(getPatchResp.repositories);
      setSkipped(allSkipped(getPatchResp.repositories));
      setExpanded(allExpandedNames(getPatchResp.repositories));
    }
  }, [getPatchResp]);

  useEffect(() => {
    if (isOpen) {
      getDcPrs();
      getPatch(`/${release}/unlabelled-commits`);
    }
  }, [patchLabelDialog]);

  useEffect(() => {
    if (isOpen) {
      setSegment("all");
      setSearchInput("");
      setQuery("");
      setIsSubmitting(false);
      setStep("select");
      setHideLeftOut(false);
      setConfirmClose(false);
      skipNextStepFocus.current = true;
      // Reopening the same release can reuse the cached response, so the fetch
      // effect may not re-fire; seed both from what is already loaded rather
      // than waiting for it.
      setSkipped(allSkipped(repositories));
      setExpanded(allExpandedNames(repositories));
    }
  }, [patchLabelDialog]);

  useEffect(() => {
    const timer = setTimeout(
      () => setQuery(searchInput.trim().toLowerCase()),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput]);

  // Moves focus with the step: onto the review's confirm button on entering
  // it, back onto step 1's on Back — skipping the first pass so it doesn't
  // fight the search-field autofocus a fresh open already does above.
  useEffect(() => {
    if (skipNextStepFocus.current) {
      skipNextStepFocus.current = false;
      return;
    }
    (step === "review" ? reviewConfirmRef : step1ConfirmRef).current?.focus();
  }, [step]);

  const pendingDoublePrs = useMemo(
    () => doubleCommitPrs(getDcPrsResp?.response, releaseName),
    [getDcPrsResp, releaseName]
  );
  const services = useMemo(
    () => buildServices(repositories, pendingDoublePrs),
    [repositories, pendingDoublePrs]
  );
  const withCommits = useMemo(
    () => services.filter((service) => service.commits.length),
    [services]
  );
  const counts = useMemo(
    () => serviceCounts(withCommits, services.length),
    [withCommits, services]
  );
  const banner = useMemo(
    () => patchBanner(counts, releaseName),
    [counts, releaseName]
  );
  const visible = useMemo(
    () => filterServices(withCommits, segment, query),
    [withCommits, segment, query]
  );

  // Selection is tracked across every service, but Select/Unselect all acts on
  // what the current filter shows, matching how Expand all behaves.
  const selectedCommits = useMemo(
    () =>
      withCommits.reduce(
        (total, service) =>
          total +
          Math.max(0, service.commits.length - (skipped[service.name] || 0)),
        0
      ),
    [withCommits, skipped]
  );
  const selectedServices = useMemo(
    () =>
      withCommits.filter(
        (service) => (skipped[service.name] || 0) < service.commits.length
      ).length,
    [withCommits, skipped]
  );
  // Review step: services with nothing selected drop off entirely (they're
  // only counted above), and each keeps just its included slice — the same
  // commits.slice(skippedCount) split getPatchPayload ships from — plus a
  // double/native breakdown of that slice for the Composition row.
  const reviewServices = useMemo(
    () =>
      withCommits
        .filter(
          (service) => (skipped[service.name] || 0) < service.commits.length
        )
        .map((service) => {
          const skippedCount = skipped[service.name] || 0;
          const included = service.commits.slice(skippedCount);
          const includedDouble = included.filter(isCommitDouble).length;
          return {
            name: service.name,
            commits: service.commits,
            skippedCount,
            included,
            includedDouble,
            includedNative: included.length - includedDouble,
            leftOutCount: skippedCount,
            reviewState: includedDouble ? "double" : "neutral",
            headTag: included[0]?.commit_tag || service.version,
          };
        }),
    [withCommits, skipped]
  );
  // Whether any rendered review card actually has a left-out row — a fully
  // deselected service's skipped commits count toward the summary card's "N
  // left out" text but never render a card at all, so the toggle has nothing
  // to do for those; only reviewServices' own rows matter here.
  const anyLeftOut = useMemo(
    () => reviewServices.some((service) => service.leftOutCount > 0),
    [reviewServices]
  );
  // True commit-level split (not patchHelpers' service-level counts.native/
  // counts.double), and which single service to name when every double
  // commit in scope happens to land in just one — otherwise left unnamed.
  const composition = useMemo(() => {
    const double = reviewServices.reduce(
      (total, service) => total + service.includedDouble,
      0
    );
    const native = reviewServices.reduce(
      (total, service) => total + service.includedNative,
      0
    );
    const doubleServices = reviewServices.filter(
      (service) => service.includedDouble > 0
    );
    return {
      double,
      native,
      allInOneService:
        double > 0 && doubleServices.length === 1
          ? doubleServices[0].name
          : null,
    };
  }, [reviewServices]);
  const allVisibleSelected =
    visible.length > 0 &&
    visible.every((service) => !(skipped[service.name] || 0));
  const allSelected =
    withCommits.length > 0 &&
    withCommits.every((service) => !(skipped[service.name] || 0));
  // Both bulk buttons name the number of commits they touch. serviceCounts is a
  // count of services, not commits, so these cannot reuse it.
  const totalCommits = useMemo(
    () =>
      withCommits.reduce((total, service) => total + service.commits.length, 0),
    [withCommits]
  );
  const visibleCommits = useMemo(
    () => visible.reduce((total, service) => total + service.commits.length, 0),
    [visible]
  );
  // With no filter the two buttons would do the same thing, so only the
  // release-wide one is offered.
  const isFiltered = segment !== "all" || Boolean(query);

  // Only repos that still have something ticked can block, and only via their
  // head commit's build — deselecting a repo entirely clears its block.
  const unbuiltHeads = useMemo(
    () => unbuiltSelectedHeads(withCommits, skipped),
    [withCommits, skipped]
  );

  const noCommitsCount = services.length - withCommits.length;
  const blocked = blockedReason(
    counts,
    notReadOnly,
    selectedCommits,
    unbuiltHeads
  );
  const reason = blocked.text;
  const allExpanded =
    visible.length > 0 &&
    visible.every((service) => expanded.has(service.name));

  // Commits ship as a contiguous slice from the head down. Unticking one skips
  // it and everything newer; re-ticking a skipped one brings it and everything
  // below it back.
  const toggleCommit = (repoName, index) =>
    setSkipped((prev) => ({
      ...prev,
      [repoName]: index >= (prev[repoName] || 0) ? index + 1 : index,
    }));

  // Takes the services to act on, so the same setter serves both the
  // release-wide button and the one scoped to the filter.
  const setSelection = (services, select) =>
    setSkipped((prev) => {
      const next = { ...prev };
      services.forEach((service) => {
        next[service.name] = select ? 0 : service.commits.length;
      });
      return next;
    });

  // Backdrop, Esc, × and Cancel all come through here. Nothing is saved until Confirm, so
  // only a submit in flight holds the drawer open. Leaving the review step
  // specifically discards a selection the user built up over step 1, so that
  // one case is routed through the same confirm-dialog pattern RefreshDrawer
  // uses for its own discard-on-close (see confirmClose there) rather than a
  // native browser prompt.
  const handleClose = () => {
    if (isSubmitting) return;
    if (step === "review") {
      setConfirmClose(true);
      return;
    }
    resetDialog();
  };

  const handleDialogConfirm = async () => {
    setIsSubmitting(true);
    await createReleaseLabel(
      getPatchPayload(repositories, skipped),
      `/${releaseName}/release-labels`
    );
    setIsSubmitting(false);
  };

  const toggleService = (name) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(isOpen)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            ...patchDrawer.paper(drawerWidth),
            // Allow the snap-toggle button to protrude outside the left edge.
            overflow: "visible",
            // Only present while a snap-toggle animation is running. An
            // explicit "none" at rest — rather than omitting the key — fights
            // MUI's own inline `transition: transform ...` that drives the
            // drawer's open/close slide, leaving it stuck mid-transform.
            ...(isDrawerResizing ? { transition: "width 220ms ease-out" } : {}),
          },
        }}
        SlideProps={{ onEntered: () => searchRef.current?.focus() }}
      >
        <DrawerResizeHandle
          {...resizeHandleProps}
          ariaLabel="Resize create patch drawer"
        />
        <DrawerSnapToggle
          enabled={isDesktopDrawer}
          isWide={isDrawerWide}
          onClick={toggleDrawerWide}
          ariaLabel="Expand create patch drawer to 75% width"
          collapseLabel="Collapse create patch drawer"
          sx={{ position: "absolute", left: "-11px", top: "12px", zIndex: 9 }}
        />
        <Box sx={patchDrawer.header}>
          <Box sx={patchDrawer.headerText}>
            <Box sx={patchDrawer.titleRow}>
              <Typography
                sx={patchDrawer.title}
              >{`Create patch for ${releaseName}`}</Typography>
              {notReadOnly ? null : (
                <Chip
                  label="READ ONLY"
                  size="small"
                  sx={patchDrawer.readOnlyChip}
                />
              )}
              {step === "review" ? (
                <Chip
                  label="Step 2 of 2"
                  size="small"
                  sx={patchDrawer.stepChip}
                />
              ) : null}
            </Box>
            <Typography sx={patchDrawer.subtitle}>
              {`${selectedAppGroup} \u00b7 next label would be `}
              <Box component="span" sx={patchDrawer.subtitleVersion}>
                {nextVersion}
              </Box>
            </Typography>
          </Box>
          <Box sx={patchDrawer.headerActions}>
            <IconButton
              sx={closeButtonStyles(colors)}
              aria-label="Close create patch drawer"
              onClick={handleClose}
            >
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {step === "select" ? (
          isPatchLoading ? (
            <Box sx={patchDrawer.loadingBody}>
              <CircularProgress size={28} />
              <Typography>Loading commits…</Typography>
            </Box>
          ) : (
            <>
              {banner ? (
                <Alert
                  severity={banner.tone}
                  icon={
                    banner.tone === "info" ? (
                      <InfoOutlined fontSize="inherit" />
                    ) : (
                      <Error fontSize="inherit" />
                    )
                  }
                  sx={patchDrawer.banner(banner.tone)}
                  action={
                    banner.actionSegment ? (
                      <Button
                        size="small"
                        variant="outlined"
                        sx={patchDrawer.bannerButton(
                          segment === banner.actionSegment,
                          banner.tone
                        )}
                        onClick={() => setSegment(banner.actionSegment)}
                      >
                        {banner.actionLabel}
                      </Button>
                    ) : null
                  }
                >
                  <Typography sx={patchDrawer.bannerTitle(banner.tone)}>
                    {banner.title}
                  </Typography>
                  <Typography sx={patchDrawer.bannerBody(banner.tone)}>
                    {banner.body}
                  </Typography>
                </Alert>
              ) : null}

              {services.length ? (
                <>
                  <Box sx={patchDrawer.filterRow}>
                    <TextField
                      size="small"
                      inputRef={searchRef}
                      value={searchInput}
                      placeholder={`Search ${counts.all} services\u2026`}
                      sx={patchDrawer.search}
                      InputProps={{
                        startAdornment: <Search sx={patchDrawer.searchIcon} />,
                      }}
                      onChange={(event) => setSearchInput(event.target.value)}
                    />
                    <Tooltip
                      arrow
                      title={`${
                        allSelected ? "Clears" : "Selects"
                      } every commit in this release${
                        isFiltered
                          ? ", including the ones the filter is hiding"
                          : ""
                      }`}
                      componentsProps={{
                        tooltip: { sx: patchDrawer.segmentTooltip },
                      }}
                    >
                      <Button
                        size="small"
                        variant="outlined"
                        sx={patchDrawer.selectAllButton}
                        disabled={!withCommits.length}
                        onClick={() => setSelection(withCommits, !allSelected)}
                      >
                        {`${
                          allSelected ? "Deselect" : "Select"
                        } all ${totalCommits} commits`}
                      </Button>
                    </Tooltip>
                    {isFiltered ? (
                      <Tooltip
                        arrow
                        title={`${
                          allVisibleSelected ? "Clears" : "Selects"
                        } only the ${visibleCommits} commits the filter is showing`}
                        componentsProps={{
                          tooltip: { sx: patchDrawer.segmentTooltip },
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          sx={{ ...patchDrawer.selectAllButton, ml: 0 }}
                          disabled={!visible.length}
                          onClick={() =>
                            setSelection(visible, !allVisibleSelected)
                          }
                        >
                          {`${
                            allVisibleSelected ? "Deselect" : "Select"
                          } ${visibleCommits} shown`}
                        </Button>
                      </Tooltip>
                    ) : null}
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ ...patchDrawer.expandButton, ml: 0 }}
                      onClick={() =>
                        setExpanded(
                          allExpanded
                            ? new Set()
                            : new Set(visible.map((service) => service.name))
                        )
                      }
                    >
                      {allExpanded ? "Collapse all" : "Expand all"}
                    </Button>
                  </Box>

                  {/* Composition (what the commits are) and build status (how they
              built) are two different axes, so they get two labelled groups on
              their own row. Both drive the same exclusive `segment`, so a pick
              in one clears the other. */}
                  <Box sx={patchDrawer.filterRow2}>
                    <Box sx={patchDrawer.segmentCluster}>
                      <Tooltip
                        arrow
                        title="Filter services by what their commits are: Double commits are carried over from an earlier release; Original commits were written for this branch."
                        componentsProps={{
                          tooltip: { sx: patchDrawer.segmentTooltip },
                        }}
                      >
                        <Box sx={patchDrawer.segmentLabelWrap}>
                          <InfoOutlined sx={patchDrawer.segmentLabelIcon} />
                        </Box>
                      </Tooltip>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={segment}
                        sx={patchDrawer.segments}
                        onChange={(event, value) => value && setSegment(value)}
                      >
                        <ToggleButton
                          value="all"
                          sx={patchDrawer.segment()}
                        >{`All ${counts.commits}`}</ToggleButton>
                        <ToggleButton
                          value="double"
                          sx={patchDrawer.segment(DOUBLE_FG)}
                        >{`Double ${counts.double}`}</ToggleButton>
                        <ToggleButton
                          value="native"
                          sx={patchDrawer.segment(INFO_FG)}
                        >{`Original ${counts.native}`}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                    <Box sx={patchDrawer.segmentCluster}>
                      <Tooltip
                        arrow
                        title="Filter services by commit build status: shows services that have any commit Successful, Building (in progress), or Failed."
                        componentsProps={{
                          tooltip: { sx: patchDrawer.segmentTooltip },
                        }}
                      >
                        <Box sx={patchDrawer.segmentLabelWrap}>
                          <InfoOutlined sx={patchDrawer.segmentLabelIcon} />
                        </Box>
                      </Tooltip>
                      <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={segment}
                        sx={patchDrawer.segments}
                        onChange={(event, value) => value && setSegment(value)}
                      >
                        <ToggleButton
                          value="all"
                          sx={patchDrawer.segment()}
                        >{`All ${counts.commits}`}</ToggleButton>
                        <ToggleButton
                          value="successful"
                          sx={patchDrawer.segment()}
                        >{`Successful ${counts.successful}`}</ToggleButton>
                        <ToggleButton
                          value="pending"
                          sx={patchDrawer.segment()}
                        >{`Building ${counts.pending}`}</ToggleButton>
                        <ToggleButton
                          value="failed"
                          sx={patchDrawer.segment()}
                        >{`Failed ${counts.failed}`}</ToggleButton>
                      </ToggleButtonGroup>
                    </Box>
                  </Box>

                  <Box sx={patchDrawer.list}>
                    {visible.map((service) => (
                      <Accordion
                        key={service.name}
                        disableGutters
                        expanded={expanded.has(service.name)}
                        sx={patchDrawer.service(service.state)}
                        onChange={() => toggleService(service.name)}
                      >
                        <AccordionSummary
                          expandIcon={<ExpandMore fontSize="small" />}
                          sx={patchDrawer.serviceSummary(service.state)}
                        >
                          <Typography sx={patchDrawer.serviceName}>
                            {service.name}
                          </Typography>
                          <Box sx={patchDrawer.facetChips}>
                            {serviceBadges(service).map((badge) => (
                              <Chip
                                key={badge.label}
                                label={badge.label}
                                size="small"
                                sx={patchDrawer.facetChip(badge.tone)}
                              />
                            ))}
                          </Box>
                          <Typography sx={patchDrawer.serviceVersion}>
                            {service.version}
                          </Typography>
                        </AccordionSummary>
                        <AccordionDetails sx={patchDrawer.serviceDetails}>
                          <CommitsList
                            commits={service.commits}
                            repoName={service.name}
                            skippedCount={skipped[service.name] || 0}
                            onToggle={toggleCommit}
                          />
                        </AccordionDetails>
                      </Accordion>
                    ))}
                    {noCommitsCount ? (
                      <Typography
                        sx={patchDrawer.noCommitsLine}
                      >{`${noCommitsCount} services with no new commits`}</Typography>
                    ) : null}
                  </Box>
                </>
              ) : (
                <Typography sx={patchDrawer.emptyBody}>
                  No available commits to create a new patch.
                </Typography>
              )}
            </>
          )
        ) : (
          <Box sx={patchDrawer.reviewBody}>
            <Box sx={patchDrawer.summaryCard}>
              <Box sx={patchDrawer.summaryHeadingRow}>
                <Typography sx={patchDrawer.summaryHeading}>
                  Review this patch
                </Typography>
                <Typography sx={patchDrawer.summarySubcopy}>
                  Nothing is created until you confirm.
                </Typography>
              </Box>
              <Box sx={patchDrawer.summaryRow}>
                <Typography sx={patchDrawer.summaryKey}>New label</Typography>
                <Typography sx={patchDrawer.summaryValue}>
                  <Box component="span" sx={patchDrawer.summaryValueMono}>
                    {nextVersion}
                  </Box>
                </Typography>
              </Box>
              <Box sx={patchDrawer.summaryRow}>
                <Typography sx={patchDrawer.summaryKey}>Release</Typography>
                <Typography sx={patchDrawer.summaryValue}>
                  <Box component="span" sx={patchDrawer.summaryValueMono}>
                    {releaseName}
                  </Box>
                  <Box component="span" sx={{ color: "text.secondary" }}>
                    {` · app ${selectedAppGroup}`}
                  </Box>
                </Typography>
              </Box>
              <Box sx={patchDrawer.summaryRow}>
                <Typography sx={patchDrawer.summaryKey}>Contents</Typography>
                <Typography sx={patchDrawer.summaryValue}>
                  <Box component="span" sx={patchDrawer.summaryStrong}>
                    {`${selectedCommits} commit${
                      selectedCommits === 1 ? "" : "s"
                    }`}
                  </Box>
                  {" across "}
                  <Box component="span" sx={patchDrawer.summaryStrong}>
                    {`${selectedServices} service${
                      selectedServices === 1 ? "" : "s"
                    }`}
                  </Box>
                  {totalCommits - selectedCommits ? (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {` · ${totalCommits - selectedCommits} commit${
                        totalCommits - selectedCommits === 1 ? "" : "s"
                      } left out`}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
              <Box sx={patchDrawer.summaryRow}>
                <Typography sx={patchDrawer.summaryKey}>Composition</Typography>
                <Typography sx={patchDrawer.summaryValue}>
                  {composition.double
                    ? `${composition.native} original · `
                    : `${composition.native} original commit${
                        composition.native === 1 ? "" : "s"
                      }`}
                  {composition.double ? (
                    <Box component="span" sx={patchDrawer.summaryDouble}>
                      {`${composition.double} double commit${
                        composition.double === 1 ? "" : "s"
                      }`}
                    </Box>
                  ) : null}
                  {composition.allInOneService ? (
                    <Box component="span" sx={{ color: "text.secondary" }}>
                      {` · all in ${composition.allInOneService}`}
                    </Box>
                  ) : null}
                </Typography>
              </Box>
            </Box>

            <Box sx={patchDrawer.sectionDivider}>
              <Typography sx={patchDrawer.segmentLabel}>
                {`Included · ${selectedServices} service${
                  selectedServices === 1 ? "" : "s"
                }`}
              </Typography>
              <Box sx={patchDrawer.sectionRule} />
              {anyLeftOut ? (
                <Button
                  size="small"
                  variant="outlined"
                  sx={patchDrawer.hideLeftOutButton}
                  onClick={() => setHideLeftOut((prev) => !prev)}
                >
                  {hideLeftOut ? "Show left out" : "Hide left out"}
                </Button>
              ) : null}
            </Box>

            {reviewServices.map((service) => (
              <Box
                key={service.name}
                sx={patchDrawer.service(service.reviewState)}
              >
                <Box sx={patchDrawer.reviewServiceHeader(service.reviewState)}>
                  <Typography sx={patchDrawer.serviceName}>
                    {service.name}
                  </Typography>
                  <Box sx={patchDrawer.facetChips}>
                    <Chip
                      label={`${service.included.length} commit${
                        service.included.length === 1 ? "" : "s"
                      }`}
                      size="small"
                      sx={patchDrawer.facetChip("neutral")}
                    />
                    {service.includedDouble ? (
                      <Chip
                        label="Includes double"
                        size="small"
                        sx={patchDrawer.facetChip("double")}
                      />
                    ) : null}
                    {service.leftOutCount ? (
                      <Chip
                        label={`${service.leftOutCount} left out`}
                        size="small"
                        sx={patchDrawer.facetChip("leftOut")}
                      />
                    ) : null}
                  </Box>
                  <Typography sx={patchDrawer.reviewServiceVersion}>
                    {service.headTag}
                  </Typography>
                </Box>
                <Box sx={patchDrawer.serviceDetails}>
                  <CommitsList
                    commits={service.commits}
                    repoName={service.name}
                    skippedCount={service.skippedCount}
                    readOnly
                    hideLeftOut={hideLeftOut}
                  />
                </Box>
              </Box>
            ))}
          </Box>
        )}

        <Box sx={patchDrawer.footer}>
          <Typography sx={patchDrawer.footerStatus(Boolean(reason))}>
            {reason ||
              `Creates ${nextVersion} from ${selectedCommits} commits across ${selectedServices} services`}
            {/* Only the first couple of blocking repos are named inline; the
              rest are listed on hover of "and N more". */}
            {blocked.more.length ? (
              <>
                {" and "}
                <Tooltip
                  title={
                    <Typography>
                      {blocked.more
                        .map(({ name, status }) => `${name} — ${status}`)
                        .join("\n")}
                    </Typography>
                  }
                  componentsProps={{
                    tooltip: { sx: patchDrawer.blockedListTooltip },
                  }}
                >
                  <Box component="span" sx={patchDrawer.moreBlocked}>
                    {`${blocked.more.length} more`}
                  </Box>
                </Tooltip>
              </>
            ) : null}
          </Typography>
          <Box sx={patchDrawer.footerActions}>
            {step === "review" ? (
              <Button
                variant="outlined"
                color="inherit"
                sx={patchDrawer.cancelButton}
                onClick={() => setStep("select")}
              >
                Back
              </Button>
            ) : null}
            <Button
              variant="outlined"
              color="inherit"
              sx={patchDrawer.cancelButton}
              onClick={handleClose}
            >
              Cancel
            </Button>
            <Tooltip title={reason}>
              <span>
                {step === "review" ? (
                  <Button
                    ref={reviewConfirmRef}
                    variant="contained"
                    sx={patchDrawer.confirmButton}
                    disabled={Boolean(reason) || isSubmitting}
                    startIcon={
                      isSubmitting ? (
                        <CircularProgress size={12} color="inherit" />
                      ) : null
                    }
                    onClick={handleDialogConfirm}
                  >
                    {`Create patch \u00b7 ${selectedCommits} commits`}
                  </Button>
                ) : (
                  <Button
                    ref={step1ConfirmRef}
                    variant="contained"
                    sx={patchDrawer.confirmButton}
                    disabled={Boolean(reason)}
                    onClick={() => setStep("review")}
                  >
                    Review
                  </Button>
                )}
              </span>
            </Tooltip>
          </Box>
        </Box>
      </Drawer>

      <ConvoyDialog
        open={confirmClose}
        title="Discard this patch review?"
        confirmBtnText="Discard"
        cancelBtnText="Keep editing"
        handleDialogConfirm={() => {
          setConfirmClose(false);
          resetDialog();
        }}
        resetDialog={() => setConfirmClose(false)}
      >
        <Typography component="span" variant="h5">
          Your commit selection will be lost if you close now.
        </Typography>
      </ConvoyDialog>
    </>
  );
};

export default PatchLabelDialog;
